import { pseudoClasses } from '@liquidweb-dev/undercurrent/classes/pseudo';
import InputAdornment from '@liquidweb-dev/undercurrent/InputAdornment';
import TextField, {
	type TextFieldProps,
} from '@liquidweb-dev/undercurrent/TextField';
import Typography from '@liquidweb-dev/undercurrent/Typography';
import { mergeSlotProps } from '@mui/material';
import round from 'lodash/round';
import { ChangeEvent, FocusEvent } from 'react';

import { useInput, type FormValue } from 'components/LWForm/LWForm';

/**
 * An LWForm validator that rejects anything `Number()` cannot parse — a
 * partially typed `.`, say, which would otherwise slip through any comparison
 * against `NaN`.
 */
export const validateIsNumber = (value: FormValue<unknown>) =>
	Number.isFinite(Number(value)) ? '' : 'Please enter a valid number';

/**
 * An LWForm validator that requires a positive amount. Rules out `NaN` first,
 * so the comparison is only ever made against a real number.
 */
export const validateValueGreaterThanZero = (value: FormValue<unknown>) =>
	validateIsNumber(value) ||
	(Number(value) > 0 ? '' : 'Please enter an amount greater than 0');

/**
 * An LWForm validator that requires a USD currency to be at least a certain
 * amount, defaults to 0. Rules out `NaN` first, so the comparison is only ever
 * made against a real number.
 *
 * Note - only use with `HookedCurrencyField` since the returned message
 * references currency
 */
export const validateValueAtLeast = (value: FormValue<unknown>, minimum = 0) =>
	validateIsNumber(value) ||
	(Number(value) >= minimum
		? ''
		: `Please enter an amount that is at least $${minimum.toFixed(2)}`);

/**
 * Strips everything that isn't a digit or a decimal separator, collapses any
 * extra separators into the first one, and truncates to whole cents. Keeps
 * invalid input out of the field entirely, since a text input does no
 * sanitizing of its own.
 */
const sanitizeAmount = (amount: string) => {
	const [whole, ...fractions] = amount.replace(/[^\d.]/g, '').split('.');

	return fractions.length
		? `${whole}.${fractions.join('').slice(0, 2)}`
		: whole;
};

/**
 * Clamps to a non-negative amount and keeps the decimal places valid for
 * currency: whole numbers stay bare, anything fractional gets padded to two
 * places so a lone decimal like `25.5` can never be displayed.
 */
const formatAmount = (amount: string) => {
	const parsed = Number(amount);

	if (amount === '' || !Number.isFinite(parsed)) {
		return '';
	}

	const clamped = Math.max(round(parsed, 2), 0);

	return Number.isInteger(clamped) ? String(clamped) : clamped.toFixed(2);
};

export interface HookedCurrencyFieldProps
	extends Omit<TextFieldProps, 'select' | 'type' | 'autoComplete'> {
	/** Hide helper text generated by LWForm validation */
	suppressErrorHelperText?: boolean;
}

/**
 * A currency input component to be used within the context of LWForm.
 *
 * Formatting only — the field keeps input looking like currency but does not
 * validate it. Between keystrokes the value can be a partial amount that
 * `Number()` reads as `NaN` (a lone `.`, for instance), and normalization
 * happens on blur, which Enter can skip entirely. Disabling a submit button is
 * not enough on its own either, since Enter submits the form without it. So any
 * consumer that acts on the amount needs an LWForm validator to gate submission
 * — `validateValueGreaterThanZero` or `validateIsNumber`, both exported here:
 *
 * @example
 * <LWForm
 * 	validators={{ amount: validateValueGreaterThanZero }}
 * >
 * 	<HookedCurrencyField name="amount" />
 * </LWForm>
 */
const HookedCurrencyField = ({
	helperText,
	label,
	name,
	onBlur,
	onChange,
	placeholder,
	error: errorProp,
	required,
	slotProps,
	suppressErrorHelperText,
	...rest
}: HookedCurrencyFieldProps) => {
	const { value, onValueChange, error, validate } = useInput(name as string);

	if (!name) {
		return null;
	}

	const handleChange = (event: ChangeEvent<HTMLInputElement>) => {
		if (!event.target) {
			return;
		}

		if (onChange) {
			onChange(event);
		}

		onValueChange(sanitizeAmount(event.target.value));

		// validate on input change only if there is an existing error
		if (!error) {
			return;
		}

		validate(event.target);
	};

	const handleBlur = (event: FocusEvent<HTMLInputElement, Element>) => {
		if (!event.target) {
			return;
		}

		if (onBlur) {
			onBlur(event);
		}

		onValueChange(formatAmount(event.target.value));

		validate(event.target);
	};

	const { htmlInput, input, ...restSlotProps } = slotProps || {};
	const autoErrorHelperText = suppressErrorHelperText ? '' : error;

	return (
		<TextField
			helperText={autoErrorHelperText || errorProp || helperText}
			label={label}
			name={name}
			onBlur={handleBlur}
			onChange={handleChange}
			placeholder={placeholder}
			error={Boolean(error || errorProp)}
			required={required}
			type="text"
			autoComplete="off"
			slotProps={{
				htmlInput: mergeSlotProps(htmlInput, {
					// hints a decimal keypad on mobile
					inputMode: 'decimal',
					sx: { [pseudoClasses.important]: { paddingLeft: 0 } },
				}),
				input: mergeSlotProps(input, {
					startAdornment: (
						<InputAdornment position="start">
							<Typography
								variant="body-md"
								sx={{ color: ({ vars }) => vars.palette.uc.text.main }}
							>
								$
							</Typography>
						</InputAdornment>
					),
				}),
				...restSlotProps,
			}}
			{...rest}
			value={value}
		/>
	);
};

export default HookedCurrencyField;
